import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  CreditCard, 
  FileText, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Download, 
  Plus, 
  Search, 
  X,
  Trash2,
  Info
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { isCoachingAdmin as checkIsCoachingAdmin } from '../../utils/rbac';
import { 
  saveStudentFee,
  recordFeePayment,
  getFeeReceiptByPaymentId,
  getBatches,
  getBatchEnrollments
} from '../../firebase/collections';
import { collection, query, where, orderBy, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';

const FeeManagement = () => {
  const { currentUser, userProfile } = useRealtime();
  const [activeTab, setActiveTab] = useState('fees');
  const [loading, setLoading] = useState(true);
  const [fees, setFees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null); // Main batch selection for viewing data
  const [selectedBatchForForm, setSelectedBatchForForm] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Form states
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  const [feeForm, setFeeForm] = useState({
    studentId: '',
    batchId: '',
    feeType: 'tuition',
    amount: '',
    dueDate: '',
    academicYear: new Date().getFullYear(),
    term: '',
    description: '',
    // Late fee configuration
    lateFeeEnabled: false,
    lateFeeType: 'fixed', // fixed, perDay, percentage, slab
    lateFeeAmount: '', // For fixed and perDay
    lateFeePercentage: '', // For percentage
    lateFeeSlabs: [{ fromDay: 1, toDay: 5, amount: '' }] // For slab-based
  });

  const [paymentForm, setPaymentForm] = useState({
    feeId: '',
    amount: '',
    paymentMethod: 'cash',
    transactionId: '',
    notes: ''
  });
  
  const [recordingPayment, setRecordingPayment] = useState(false);

  const isCoachingAdmin = checkIsCoachingAdmin(userProfile, currentUser);
  const coachingId = userProfile?.coachingId || null;

  // Helper function to calculate late fee
  const calculateLateFee = (fee, paymentDate = new Date()) => {
    if (!fee.lateFeeEnabled || !fee.dueDate) return 0;
    
    const dueDate = fee.dueDate?.toDate ? fee.dueDate.toDate() : new Date(fee.dueDate);
    const now = paymentDate;
    
    if (now <= dueDate) return 0; // Not late
    
    const daysLate = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
    if (daysLate <= 0) return 0;
    
    const feeAmount = parseFloat(fee.amount) || 0;
    
    switch (fee.lateFeeType) {
      case 'fixed':
        return parseFloat(fee.lateFeeAmount) || 0;
      
      case 'perDay':
        return daysLate * (parseFloat(fee.lateFeeAmount) || 0);
      
      case 'percentage':
        return (feeAmount * (parseFloat(fee.lateFeePercentage) || 0)) / 100;
      
      case 'slab':
        if (!fee.lateFeeSlabs || fee.lateFeeSlabs.length === 0) return 0;
        // Find the applicable slab
        const sortedSlabs = [...fee.lateFeeSlabs].sort((a, b) => a.fromDay - b.fromDay);
        for (const slab of sortedSlabs) {
          if (daysLate >= slab.fromDay && (slab.toDay === null || slab.toDay === '' || daysLate <= slab.toDay)) {
            return parseFloat(slab.amount) || 0;
          }
        }
        // If days exceed all slabs, use the last slab's amount
        const lastSlab = sortedSlabs[sortedSlabs.length - 1];
        if (daysLate > (lastSlab.toDay || 0)) {
          return parseFloat(lastSlab.amount) || 0;
        }
        return 0;
      
      default:
        return 0;
    }
  };

  // Helper to add a new slab
  const addLateFeeSlabs = () => {
    const slabs = feeForm.lateFeeSlabs;
    const lastSlab = slabs[slabs.length - 1];
    const newFromDay = lastSlab.toDay ? parseInt(lastSlab.toDay) + 1 : 1;
    setFeeForm({
      ...feeForm,
      lateFeeSlabs: [...slabs, { fromDay: newFromDay, toDay: '', amount: '' }]
    });
  };

  // Helper to remove a slab
  const removeLateFeeSlabs = (index) => {
    if (feeForm.lateFeeSlabs.length <= 1) return;
    const newSlabs = feeForm.lateFeeSlabs.filter((_, i) => i !== index);
    setFeeForm({ ...feeForm, lateFeeSlabs: newSlabs });
  };

  // Helper to update a slab
  const updateLateFeeSlabs = (index, field, value) => {
    const newSlabs = [...feeForm.lateFeeSlabs];
    newSlabs[index] = { ...newSlabs[index], [field]: value };
    setFeeForm({ ...feeForm, lateFeeSlabs: newSlabs });
  };

  useEffect(() => {
    if (coachingId) {
      loadBatches();
    } else {
      setLoading(false);
    }
  }, [coachingId]);

  // Load data when batch is selected
  useEffect(() => {
    if (coachingId && selectedBatch) {
      loadData();
    } else {
      // Clear data when no batch is selected
      setFees([]);
      setPayments([]);
      setOutstanding([]);
      setLoading(false);
    }
  }, [coachingId, selectedBatch]);

  const loadBatches = async () => {
    try {
      const batchesResult = await getBatches(coachingId);
      if (batchesResult.success) setBatches(batchesResult.data || []);
    } catch (error) {
      toast.error('Failed to load batches');
    }
  };

  const loadData = async () => {
    const coachingId = userProfile?.coachingId;
    const batchId = selectedBatch;
    if (!coachingId || !batchId) {
      setLoading(false);
      return;
    }

    console.log('Loading data for batch:', batchId, 'coachingId:', coachingId);
    setLoading(true);
    try {
      // Load fees
      let feesSnapshot;
      try {
        const feesQuery = query(
          collection(db, COLLECTIONS.STUDENT_FEES),
          where('coachingId', '==', coachingId),
          where('batchId', '==', batchId),
          orderBy('createdAt', 'desc')
        );
        feesSnapshot = await getDocs(feesQuery);
      } catch (error) {
        // If index error, try fallback query without orderBy
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          console.log('Index not available, using fallback query for fees');
          const fallbackQuery = query(
            collection(db, COLLECTIONS.STUDENT_FEES),
            where('coachingId', '==', coachingId),
            where('batchId', '==', batchId)
          );
          feesSnapshot = await getDocs(fallbackQuery);
        } else {
          throw error;
        }
      }
      console.log('Fees snapshot size:', feesSnapshot.docs.length);
      const feesList = [];
      for (const docSnap of feesSnapshot.docs) {
        const feeData = { id: docSnap.id, ...docSnap.data() };
        // Get student details
        try {
          const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, feeData.studentId));
          if (userDoc.exists()) {
            feeData.studentData = userDoc.data();
          }
        } catch (err) {
          // Student not found
        }
        feesList.push(feeData);
      }
      // Sort by createdAt if not already sorted
      feesList.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      setFees(feesList);
      
      // Calculate outstanding fees (all fees with outstanding amount > 0)
      const now = new Date();
      const outstandingList = feesList
        .filter(fee => fee.outstandingAmount > 0)
        .map(fee => {
          const dueDate = fee.dueDate?.toDate ? fee.dueDate.toDate() : new Date(fee.dueDate);
          const isOverdue = dueDate < now;
          const daysOverdue = isOverdue 
            ? Math.floor((now - dueDate) / (1000 * 60 * 60 * 24))
            : 0;
          return {
            ...fee,
            daysOverdue,
            isOverdue
          };
        });
      setOutstanding(outstandingList);

      // Load payments
      let paymentsSnapshot;
      try {
        const paymentsQuery = query(
          collection(db, COLLECTIONS.FEE_PAYMENTS),
          where('coachingId', '==', coachingId),
          where('batchId', '==', batchId),
          orderBy('paidAt', 'desc')
        );
        paymentsSnapshot = await getDocs(paymentsQuery);
      } catch (error) {
        // If index error, try fallback query without orderBy
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          console.log('Index not available, using fallback query for payments');
          const fallbackQuery = query(
            collection(db, COLLECTIONS.FEE_PAYMENTS),
            where('coachingId', '==', coachingId),
            where('batchId', '==', batchId)
          );
          paymentsSnapshot = await getDocs(fallbackQuery);
        } else {
          throw error;
        }
      }
      console.log('Payments snapshot size:', paymentsSnapshot.docs.length);
      const paymentsList = [];
      for (const docSnap of paymentsSnapshot.docs) {
        const paymentData = { id: docSnap.id, ...docSnap.data() };
        // Get student details
        try {
          const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, paymentData.studentId));
          if (userDoc.exists()) {
            paymentData.studentData = userDoc.data();
          }
        } catch (err) {
          // Student not found
        }
        paymentsList.push(paymentData);
      }
      // Sort by paidAt if not already sorted
      paymentsList.sort((a, b) => {
        const dateA = a.paidAt?.toDate ? a.paidAt.toDate() : new Date(a.paidAt || 0);
        const dateB = b.paidAt?.toDate ? b.paidAt.toDate() : new Date(b.paidAt || 0);
        return dateB - dateA;
      });
      setPayments(paymentsList);
      console.log('Data loaded successfully. Fees:', feesList.length, 'Payments:', paymentsList.length);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data: ' + (error.message || error.code || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async (batchId) => {
    if (!batchId) {
      setStudents([]);
      return;
    }
    
    try {
      setLoadingStudents(true);
      
      // Get enrollments first
      const enrollmentsResult = await getBatchEnrollments(batchId);
      
      if (!enrollmentsResult.success || !enrollmentsResult.data || enrollmentsResult.data.length === 0) {
        setStudents([]);
        toast.error('No students enrolled in this batch');
        return;
      }
      
      // Extract emails from enrollments
      const emails = [];
      enrollmentsResult.data.forEach((enrollment) => {
        const email = enrollment.email || enrollment.studentEmail || enrollment.userData?.email;
        if (email) {
          emails.push(email);
        }
      });
      
      if (emails.length === 0) {
        setStudents([]);
        toast.error('Enrollment records are missing email addresses');
        return;
      }
      
      // Query users by email
      const batchStudents = [];
      
      for (const email of emails) {
        try {
          const userQuery = query(
            collection(db, COLLECTIONS.USERS),
            where('email', '==', email)
          );
          const userSnapshot = await getDocs(userQuery);
          
          if (!userSnapshot.empty) {
            const userDoc = userSnapshot.docs[0];
            const userData = userDoc.data();
            batchStudents.push({
              id: userDoc.id,
              ...userData,
              batchId: batchId,
              batchName: batches.find(b => b.id === batchId)?.name || ''
            });
          }
        } catch (err) {
          console.error('Error querying email:', err);
        }
      }
      
      setStudents(batchStudents);
      
      if (batchStudents.length === 0) {
        toast.error('No student users found for enrolled emails');
      } else {
        toast.success(`Loaded ${batchStudents.length} students`);
      }
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students: ' + error.message);
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleBatchChange = (batchId) => {
    setSelectedBatchForForm(batchId);
    setFeeForm({ ...feeForm, batchId: batchId || '', studentId: '' });
    setStudents([]); // Clear students immediately
    if (batchId) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        loadStudents(batchId);
      }, 100);
    }
  };

  const handleSaveFee = async () => {
    if (!feeForm.studentId || !feeForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate late fee configuration
    if (feeForm.lateFeeEnabled) {
      if (!feeForm.dueDate) {
        toast.error('Due date is required when late fee is enabled');
        return;
      }
      if (feeForm.lateFeeType === 'fixed' || feeForm.lateFeeType === 'perDay') {
        if (!feeForm.lateFeeAmount || parseFloat(feeForm.lateFeeAmount) <= 0) {
          toast.error('Please enter a valid late fee amount');
          return;
        }
      }
      if (feeForm.lateFeeType === 'percentage') {
        if (!feeForm.lateFeePercentage || parseFloat(feeForm.lateFeePercentage) <= 0) {
          toast.error('Please enter a valid late fee percentage');
          return;
        }
      }
      if (feeForm.lateFeeType === 'slab') {
        const invalidSlab = feeForm.lateFeeSlabs.some(slab => 
          !slab.fromDay || !slab.amount || parseFloat(slab.amount) <= 0
        );
        if (invalidSlab) {
          toast.error('Please fill all slab details correctly');
          return;
        }
      }
    }

    try {
      const result = await saveStudentFee({
        ...feeForm,
        coachingId: userProfile?.coachingId,
        batchId: selectedBatchForForm || selectedBatch,
        dueDate: feeForm.dueDate ? new Date(feeForm.dueDate) : null,
        // Late fee configuration
        lateFeeEnabled: feeForm.lateFeeEnabled,
        lateFeeType: feeForm.lateFeeEnabled ? feeForm.lateFeeType : null,
        lateFeeAmount: feeForm.lateFeeEnabled && (feeForm.lateFeeType === 'fixed' || feeForm.lateFeeType === 'perDay') 
          ? parseFloat(feeForm.lateFeeAmount) : null,
        lateFeePercentage: feeForm.lateFeeEnabled && feeForm.lateFeeType === 'percentage' 
          ? parseFloat(feeForm.lateFeePercentage) : null,
        lateFeeSlabs: feeForm.lateFeeEnabled && feeForm.lateFeeType === 'slab' 
          ? feeForm.lateFeeSlabs.map(slab => ({
              fromDay: parseInt(slab.fromDay),
              toDay: slab.toDay ? parseInt(slab.toDay) : null,
              amount: parseFloat(slab.amount)
            }))
          : null
      });

      if (result.success) {
        toast.success('Fee created successfully');
        setShowFeeForm(false);
        setSelectedBatchForForm(null);
        setFeeForm({
          studentId: '',
          batchId: '',
          feeType: 'tuition',
          amount: '',
          dueDate: '',
          academicYear: new Date().getFullYear(),
          term: '',
          description: '',
          lateFeeEnabled: false,
          lateFeeType: 'fixed',
          lateFeeAmount: '',
          lateFeePercentage: '',
          lateFeeSlabs: [{ fromDay: 1, toDay: 5, amount: '' }]
        });
        setStudents([]);
        await loadData();
      } else {
        toast.error(result.error || 'Failed to create fee');
      }
    } catch (error) {
      toast.error('Failed to create fee');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.feeId || !paymentForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Prevent duplicate submissions
    if (recordingPayment) {
      return;
    }

    // Validate payment amount
    const paymentAmount = parseFloat(paymentForm.amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    // Calculate late fee and total due
    const lateFee = selectedFee ? calculateLateFee(selectedFee) : 0;
    const totalDue = (selectedFee?.outstandingAmount || 0) + lateFee;

    // Warn if payment exceeds total due
    if (paymentAmount > totalDue) {
      const confirmed = window.confirm(
        `Payment amount (₹${paymentAmount.toLocaleString()}) exceeds total due (₹${totalDue.toLocaleString()}). Do you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    setRecordingPayment(true);

    try {
      const result = await recordFeePayment({
        ...paymentForm,
        studentId: selectedFee?.studentId,
        coachingId: userProfile?.coachingId,
        paymentId: paymentForm.transactionId || `PAY-${Date.now()}`,
        // Include late fee details
        lateFeeApplied: lateFee > 0 ? lateFee : 0,
        lateFeeIncluded: lateFee > 0 && paymentAmount > selectedFee?.outstandingAmount
      });

      if (result.success) {
        toast.success('Payment recorded successfully');
        setShowPaymentForm(false);
        setPaymentForm({
          feeId: '',
          amount: '',
          paymentMethod: 'cash',
          transactionId: '',
          notes: ''
        });
        setSelectedFee(null);
        await loadData();
      } else {
        toast.error(result.error || 'Failed to record payment');
      }
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      // First try to get existing receipt
      let receiptResult = await getFeeReceiptByPaymentId(paymentId);
      
      if (!receiptResult.success) {
        // Receipt doesn't exist, generate it from payment data
        console.log('Receipt not found, generating from payment data...');
        
        // Find the payment in the payments list
        const payment = payments.find(p => p.id === paymentId);
        if (!payment) {
          toast.error('Payment not found');
          return;
        }

        // Get fee data
        const fee = fees.find(f => f.id === payment.feeId);
        if (!fee) {
          toast.error('Fee record not found');
          return;
        }

        // Generate receipt data on the fly
        const receiptData = {
          receiptNumber: `REC-${Date.now()}`,
          paymentId: payment.id,
          feeId: fee.id,
          studentId: payment.studentId,
          coachingId: payment.coachingId,
          studentName: payment.studentData?.name || payment.studentData?.displayName || fee.studentData?.name || 'Student',
          coachingName: userProfile?.coachingName || userProfile?.name || 'School',
          amount: payment.amount,
          paymentMethod: payment.paymentMethod,
          transactionId: payment.transactionId,
          feeType: fee.feeType,
          academicYear: fee.academicYear,
          term: fee.term,
          issuedAt: payment.paidAt || new Date(),
          lateFeeAmount: payment.lateFeeAmount || payment.lateFeeApplied || 0,
          totalFeeAmount: fee.amount || fee.totalAmount || 0,
          outstandingAmount: fee.outstandingAmount || 0,
          status: fee.status || 'pending',
        };

        generateReceiptPDF(receiptData);
        toast.success('Receipt generated and downloaded');
      } else {
        generateReceiptPDF(receiptResult.data);
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt: ' + error.message);
    }
  };

  // Helper function to add MahStry branding in top right corner
  const addMahStryBranding = (doc, pageWidth) => {
    // Removed - no watermark
  };

  const generateReceiptPDF = (receiptData) => {
    const doc = new jsPDF();
    const W = 210;
    const lm = 20;
    const rm = 20;
    const textWidth = W - lm - rm;

    // Border
    doc.setDrawColor(30, 80, 160);
    doc.setLineWidth(1.5);
    doc.rect(10, 10, W - 20, 277);

    // Header - Institute Name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(30, 80, 160);
    doc.text(receiptData.coachingName || 'Institution', W / 2, 25, { align: 'center' });

    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text('FEE PAYMENT RECEIPT', W / 2, 33, { align: 'center' });

    // Divider
    doc.setDrawColor(30, 80, 160);
    doc.setLineWidth(0.8);
    doc.line(lm, 37, W - rm, 37);

    let y = 45;

    // Receipt No & Date row
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Receipt No: ${receiptData.receiptNumber || 'N/A'}`, lm, y);
    let receiptDate = new Date();
    if (receiptData.issuedAt) {
      if (receiptData.issuedAt.toDate) receiptDate = receiptData.issuedAt.toDate();
      else if (receiptData.issuedAt instanceof Date) receiptDate = receiptData.issuedAt;
      else receiptDate = new Date(receiptData.issuedAt);
    }
    doc.text(`Date: ${receiptDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, W - rm, y, { align: 'right' });
    y += 12;

    // Student Details Section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Student Details', lm, y);
    y += 2;
    doc.setLineWidth(0.3);
    doc.line(lm, y, lm + 35, y);
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const studentFields = [
      ['Student Name', receiptData.studentName || 'N/A'],
      ['Fee Type', receiptData.feeType || 'N/A'],
      ['Academic Year', receiptData.academicYear || 'N/A'],
      ['Term', receiptData.term || 'N/A'],
    ];
    studentFields.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, lm, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), lm + 45, y);
      y += 7;
    });
    y += 5;

    // Payment Details Section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Payment Details', lm, y);
    y += 2;
    doc.setLineWidth(0.3);
    doc.line(lm, y, lm + 40, y);
    y += 7;

    doc.setFontSize(10);
    const amount = typeof receiptData.amount === 'number' ? receiptData.amount : parseFloat(receiptData.amount) || 0;
    const lateFee = receiptData.lateFeeAmount || receiptData.lateFeeApplied || 0;
    const baseAmount = lateFee > 0 ? amount - lateFee : amount;

    const paymentFields = [
      ['Base Fee Amount', `Rs. ${baseAmount.toLocaleString('en-IN')}`],
    ];
    if (lateFee > 0) {
      paymentFields.push(['Late Fee', `Rs. ${lateFee.toLocaleString('en-IN')}`]);
    }
    paymentFields.push(
      ['Total Amount Paid', `Rs. ${amount.toLocaleString('en-IN')}`],
      ['Payment Method', receiptData.paymentMethod || 'N/A'],
      ['Transaction ID', receiptData.transactionId || 'N/A'],
      ['Payment Date', receiptDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })],
    );

    if (receiptData.totalFeeAmount) {
      paymentFields.push(['Total Fee (Session)', `Rs. ${Number(receiptData.totalFeeAmount).toLocaleString('en-IN')}`]);
    }
    if (receiptData.outstandingAmount !== undefined) {
      paymentFields.push(['Outstanding Balance', `Rs. ${Number(receiptData.outstandingAmount).toLocaleString('en-IN')}`]);
    }
    if (receiptData.status) {
      paymentFields.push(['Fee Status', receiptData.status.toUpperCase()]);
    }

    paymentFields.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, lm, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), lm + 50, y);
      y += 7;
    });

    // Amount in words
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Amount in Words:', lm, y);
    doc.setFont('helvetica', 'normal');
    doc.text(numberToWords(amount), lm + 42, y);
    y += 12;

    // Signature
    const sigY = Math.max(y + 20, 240);
    doc.setLineWidth(0.5);
    doc.line(W - 80, sigY, W - rm, sigY);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Authorized Signatory', W - rm, sigY + 5, { align: 'right' });

    // Footer
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.3);
    doc.line(lm, 275, W - rm, 275);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'italic');
    doc.text('This is a computer-generated receipt and does not require a physical signature.', W / 2, 280, { align: 'center' });

    doc.save(`Receipt-${receiptData.receiptNumber || 'Receipt'}.pdf`);
    toast.success('Receipt downloaded');
  };

  // Convert number to words for receipt
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const n = Math.floor(num);
    if (n < 20) return ones[n] + ' Rupees Only';
    if (n < 100) return (tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '')) + ' Rupees Only';
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + (n % 100 ? numberToWords(n % 100).replace(' Rupees Only', '') : '') + ' Rupees Only';
    if (n < 100000) return numberToWords(Math.floor(n / 1000)).replace(' Rupees Only', '') + ' Thousand ' + (n % 1000 ? numberToWords(n % 1000).replace(' Rupees Only', '') : '') + ' Rupees Only';
    if (n < 10000000) return numberToWords(Math.floor(n / 100000)).replace(' Rupees Only', '') + ' Lakh ' + (n % 100000 ? numberToWords(n % 100000).replace(' Rupees Only', '') : '') + ' Rupees Only';
    return numberToWords(Math.floor(n / 10000000)).replace(' Rupees Only', '') + ' Crore ' + (n % 10000000 ? numberToWords(n % 10000000).replace(' Rupees Only', '') : '') + ' Rupees Only';
  };

  const filteredFees = fees.filter(fee => {
    const matchesSearch = 
      fee.studentData?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fee.studentData?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fee.feeType?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || fee.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Calculate summaries from all fees and payments
  const totalOutstanding = fees.reduce((sum, fee) => sum + (fee.outstandingAmount || 0), 0);
  const totalPaid = fees.reduce((sum, fee) => sum + (fee.paidAmount || 0), 0);
  const overdueCount = outstanding.length;
  const totalPaymentsCount = payments.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Fee Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage student fees, payments, and receipts
        </p>
      </div>

      {/* Batch Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Batch
        </label>
        <select
          value={selectedBatch || ''}
          onChange={(e) => {
            const batchId = e.target.value;
            setSelectedBatch(batchId || null);
            setFees([]);
            setPayments([]);
            setOutstanding([]);
            setStudents([]);
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        >
          <option value="">Select a batch</option>
          {batches.map(batch => (
            <option key={batch.id} value={batch.id}>
              {batch.name}
            </option>
          ))}
        </select>
        {batches.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            No batches available. Please create a batch first.
          </p>
        )}
      </div>

      {/* Show message if no batch selected */}
      {!selectedBatch && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <DollarSign className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Select a Batch to View Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Please select a batch from the dropdown above to view and manage fees, payments, and receipts.
          </p>
        </div>
      )}

      {/* Stats Cards - Only show when batch is selected */}
      {selectedBatch && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Outstanding</p>
              <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
                ₹{totalOutstanding.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
              <AlertCircle className="text-red-600" size={24} />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Paid</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                ₹{totalPaid.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Overdue Fees</p>
              <p className="text-3xl font-bold text-orange-600 dark:text-orange-400 mt-2">
                {overdueCount}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
              <Clock className="text-orange-600" size={24} />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Payments</p>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                {totalPaymentsCount}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <CreditCard className="text-blue-600" size={24} />
            </div>
          </div>
        </motion.div>
      </div>
      )}

      {/* Tabs - Only show when batch is selected */}
      {selectedBatch && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            {[
              { id: 'fees', label: 'All Fees', icon: FileText },
              { id: 'outstanding', label: 'Outstanding', icon: AlertCircle },
              { id: 'payments', label: 'Payments', icon: CreditCard }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-4 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon size={18} className="mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search by student name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              {activeTab === 'fees' && (
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {activeTab === 'fees' && (
                <button
                  onClick={() => setShowFeeForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={18} className="mr-2" />
                  Add Fee
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
            </div>
          ) : (
            <>
              {activeTab === 'fees' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Fee Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Paid</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Outstanding</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Due Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredFees.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                            No fees found
                          </td>
                        </tr>
                      ) : (
                        filteredFees.map(fee => (
                          <tr key={fee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {fee.studentData?.name || fee.studentData?.displayName || 'Unknown'}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {fee.studentData?.email}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white capitalize">
                              <div className="flex items-center">
                                {fee.feeType}
                                {fee.lateFeeEnabled && (
                                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 rounded" title="Late fee enabled">
                                    LF
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              ₹{fee.amount?.toLocaleString() || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400">
                              ₹{fee.paidAmount?.toLocaleString() || 0}
                              {fee.lateFeePaid > 0 && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  (incl. ₹{fee.lateFeePaid?.toLocaleString()} late fee)
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                              ₹{fee.outstandingAmount?.toLocaleString() || 0}
                              {fee.lateFeeEnabled && calculateLateFee(fee) > 0 && fee.outstandingAmount > 0 && (
                                <div className="text-xs text-orange-600 dark:text-orange-400">
                                  +₹{calculateLateFee(fee).toLocaleString()} late fee
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {fee.dueDate?.toDate ? fee.dueDate.toDate().toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                fee.status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                fee.status === 'overdue' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              }`}>
                                {fee.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              {fee.outstandingAmount > 0 && (
                                <button
                                  onClick={() => {
                                    setSelectedFee(fee);
                                    const lateFee = calculateLateFee(fee);
                                    setPaymentForm({ ...paymentForm, feeId: fee.id, amount: fee.outstandingAmount + lateFee });
                                    setShowPaymentForm(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400"
                                >
                                  Record Payment
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'outstanding' && (
                <div className="space-y-4">
                  {outstanding.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      No outstanding fees
                    </div>
                  ) : (
                    outstanding.map(fee => {
                      const lateFee = calculateLateFee(fee);
                      return (
                        <div key={fee.id} className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-gray-900 dark:text-white">
                                {fee.studentData?.name || fee.studentData?.displayName || 'Unknown'}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {fee.feeType} {fee.isOverdue ? `- ${fee.daysOverdue} days overdue` : `- Due: ${fee.dueDate?.toDate ? fee.dueDate.toDate().toLocaleDateString() : 'N/A'}`}
                              </p>
                              {fee.lateFeeEnabled && lateFee > 0 && (
                                <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                  Late fee: ₹{lateFee.toLocaleString()} ({fee.lateFeeType === 'fixed' ? 'Fixed' : fee.lateFeeType === 'perDay' ? 'Per day' : fee.lateFeeType === 'percentage' ? 'Percentage' : 'Slab'})
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-red-600 dark:text-red-400">
                                ₹{fee.outstandingAmount?.toLocaleString()}
                              </p>
                              {fee.lateFeeEnabled && lateFee > 0 && (
                                <p className="text-sm text-orange-600 dark:text-orange-400">
                                  +₹{lateFee.toLocaleString()} = ₹{(fee.outstandingAmount + lateFee).toLocaleString()}
                                </p>
                              )}
                              <button
                                onClick={() => {
                                  setSelectedFee(fee);
                                  setPaymentForm({ ...paymentForm, feeId: fee.id, amount: fee.outstandingAmount + lateFee });
                                  setShowPaymentForm(true);
                                }}
                                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                              >
                                Record Payment
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {activeTab === 'payments' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Method</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Transaction ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {payments.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                            No payments found
                          </td>
                        </tr>
                      ) : (
                        payments.map(payment => (
                          <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {payment.paidAt?.toDate ? payment.paidAt.toDate().toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {payment.studentData?.name || payment.studentData?.displayName || 'Unknown'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600 dark:text-green-400">
                              ₹{payment.amount?.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white capitalize">
                              {payment.paymentMethod}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {payment.transactionId || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => handleDownloadReceipt(payment.id)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 flex items-center"
                              >
                                <Download size={16} className="mr-1" />
                                Receipt
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      )}

      {/* Add Fee Modal */}
      {showFeeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add Fee</h2>
              <button
                onClick={() => {
                  setShowFeeForm(false);
                  setSelectedBatchForForm(null);
                  setStudents([]);
                  setFeeForm({
                    studentId: '',
                    batchId: '',
                    feeType: 'tuition',
                    amount: '',
                    dueDate: '',
                    academicYear: new Date().getFullYear(),
                    term: '',
                    description: '',
                    lateFeeEnabled: false,
                    lateFeeType: 'fixed',
                    lateFeeAmount: '',
                    lateFeePercentage: '',
                    lateFeeSlabs: [{ fromDay: 1, toDay: 5, amount: '' }]
                  });
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Pre-select batch if one is already selected */}
              {selectedBatch && !selectedBatchForForm && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Using batch: <strong>{batches.find(b => b.id === selectedBatch)?.name}</strong>
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBatchForForm(selectedBatch);
                      loadStudents(selectedBatch);
                    }}
                    className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Use this batch
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Batch *
                </label>
                <select
                  value={selectedBatchForForm || ''}
                  onChange={(e) => handleBatchChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Select Batch</option>
                  {batches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                      {batch.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Student *
                </label>
                <select
                  value={feeForm.studentId}
                  onChange={(e) => setFeeForm({ ...feeForm, studentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                  disabled={!selectedBatchForForm || loadingStudents}
                >
                  <option value="">
                    {loadingStudents ? 'Loading students...' : selectedBatchForForm ? (students.length === 0 ? 'No students in this batch' : 'Select Student') : 'Please select a batch first'}
                  </option>
                  {students.length > 0 && students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.name || student.displayName || student.fullName || 'Unknown'} - {student.email || 'No email'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fee Type *
                </label>
                <select
                  value={feeForm.feeType}
                  onChange={(e) => setFeeForm({ ...feeForm, feeType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="tuition">Tuition Fee</option>
                  <option value="exam">Exam Fee</option>
                  <option value="library">Library Fee</option>
                  <option value="transport">Transport Fee</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Amount (₹) *
                  </label>
                  <input
                    type="number"
                    value={feeForm.amount}
                    onChange={(e) => setFeeForm({ ...feeForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={feeForm.dueDate}
                    onChange={(e) => setFeeForm({ ...feeForm, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Academic Year
                  </label>
                  <input
                    type="number"
                    value={feeForm.academicYear}
                    onChange={(e) => setFeeForm({ ...feeForm, academicYear: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Term
                  </label>
                  <select
                    value={feeForm.term}
                    onChange={(e) => setFeeForm({ ...feeForm, term: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Term</option>
                    <option value="term1">Term 1</option>
                    <option value="term2">Term 2</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={feeForm.description}
                  onChange={(e) => setFeeForm({ ...feeForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Late Fee Configuration */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Enable Late Fee
                    </label>
                    <div className="relative ml-2 group">
                      <Info size={16} className="text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                        Late fee will be automatically applied after due date
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={feeForm.lateFeeEnabled}
                      onChange={(e) => setFeeForm({ ...feeForm, lateFeeEnabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {feeForm.lateFeeEnabled && (
                  <div className="space-y-4 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                    {!feeForm.dueDate && (
                      <div className="flex items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <AlertCircle size={18} className="text-yellow-600 dark:text-yellow-400 mr-2" />
                        <span className="text-sm text-yellow-800 dark:text-yellow-200">
                          Please set a due date above to enable late fee calculation
                        </span>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Late Fee Type *
                      </label>
                      <select
                        value={feeForm.lateFeeType}
                        onChange={(e) => setFeeForm({ ...feeForm, lateFeeType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="fixed">Fixed Amount (One-time)</option>
                        <option value="perDay">Per Day Late Fee</option>
                        <option value="percentage">Percentage of Fee</option>
                        <option value="slab">Slab Based</option>
                      </select>
                    </div>

                    {/* Fixed Amount */}
                    {feeForm.lateFeeType === 'fixed' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Late Fee Amount (₹) *
                        </label>
                        <input
                          type="number"
                          value={feeForm.lateFeeAmount}
                          onChange={(e) => setFeeForm({ ...feeForm, lateFeeAmount: e.target.value })}
                          placeholder="e.g., 500"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          This fixed amount will be charged once if payment is late
                        </p>
                      </div>
                    )}

                    {/* Per Day */}
                    {feeForm.lateFeeType === 'perDay' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Amount Per Day (₹) *
                        </label>
                        <input
                          type="number"
                          value={feeForm.lateFeeAmount}
                          onChange={(e) => setFeeForm({ ...feeForm, lateFeeAmount: e.target.value })}
                          placeholder="e.g., 50"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Late fee = Days late × Amount per day
                        </p>
                      </div>
                    )}

                    {/* Percentage */}
                    {feeForm.lateFeeType === 'percentage' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Late Fee Percentage (%) *
                        </label>
                        <input
                          type="number"
                          value={feeForm.lateFeePercentage}
                          onChange={(e) => setFeeForm({ ...feeForm, lateFeePercentage: e.target.value })}
                          placeholder="e.g., 5"
                          min="0"
                          max="100"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Late fee = Fee amount × Percentage / 100
                        </p>
                      </div>
                    )}

                    {/* Slab Based */}
                    {feeForm.lateFeeType === 'slab' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Late Fee Slabs *
                        </label>
                        <div className="space-y-3">
                          {feeForm.lateFeeSlabs.map((slab, index) => (
                            <div key={index} className="flex items-center gap-2 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex-1">
                                <label className="text-xs text-gray-500 dark:text-gray-400">From Day</label>
                                <input
                                  type="number"
                                  value={slab.fromDay}
                                  onChange={(e) => updateLateFeeSlabs(index, 'fromDay', e.target.value)}
                                  min="1"
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                                />
                              </div>
                              <div className="flex-1">
                                <label className="text-xs text-gray-500 dark:text-gray-400">To Day</label>
                                <input
                                  type="number"
                                  value={slab.toDay}
                                  onChange={(e) => updateLateFeeSlabs(index, 'toDay', e.target.value)}
                                  placeholder="∞"
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                                />
                              </div>
                              <div className="flex-1">
                                <label className="text-xs text-gray-500 dark:text-gray-400">Amount (₹)</label>
                                <input
                                  type="number"
                                  value={slab.amount}
                                  onChange={(e) => updateLateFeeSlabs(index, 'amount', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                                />
                              </div>
                              {feeForm.lateFeeSlabs.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeLateFeeSlabs(index)}
                                  className="mt-4 p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={addLateFeeSlabs}
                            className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                          >
                            <Plus size={16} className="mr-1" />
                            Add Slab
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Leave "To Day" empty for the last slab to apply for all days beyond
                        </p>
                      </div>
                    )}

                    {/* Preview */}
                    {feeForm.amount && feeForm.dueDate && (
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Late Fee Preview</h4>
                        <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                          {feeForm.lateFeeType === 'fixed' && feeForm.lateFeeAmount && (
                            <p>If late: ₹{parseFloat(feeForm.amount).toLocaleString()} + ₹{parseFloat(feeForm.lateFeeAmount).toLocaleString()} = ₹{(parseFloat(feeForm.amount) + parseFloat(feeForm.lateFeeAmount)).toLocaleString()}</p>
                          )}
                          {feeForm.lateFeeType === 'perDay' && feeForm.lateFeeAmount && (
                            <p>Example (10 days late): ₹{parseFloat(feeForm.amount).toLocaleString()} + (10 × ₹{parseFloat(feeForm.lateFeeAmount).toLocaleString()}) = ₹{(parseFloat(feeForm.amount) + 10 * parseFloat(feeForm.lateFeeAmount)).toLocaleString()}</p>
                          )}
                          {feeForm.lateFeeType === 'percentage' && feeForm.lateFeePercentage && (
                            <p>If late: ₹{parseFloat(feeForm.amount).toLocaleString()} + {feeForm.lateFeePercentage}% = ₹{(parseFloat(feeForm.amount) * (1 + parseFloat(feeForm.lateFeePercentage) / 100)).toLocaleString()}</p>
                          )}
                          {feeForm.lateFeeType === 'slab' && feeForm.lateFeeSlabs.some(s => s.amount) && (
                            <div>
                              {feeForm.lateFeeSlabs.filter(s => s.fromDay && s.amount).map((slab, i) => (
                                <p key={i}>
                                  Days {slab.fromDay}-{slab.toDay || '∞'}: ₹{parseFloat(feeForm.amount).toLocaleString()} + ₹{parseFloat(slab.amount).toLocaleString()} = ₹{(parseFloat(feeForm.amount) + parseFloat(slab.amount)).toLocaleString()}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowFeeForm(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFee}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Fee
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Record Payment</h2>
              <button
                onClick={() => setShowPaymentForm(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Late Fee Breakdown */}
            {selectedFee && (
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Fee Breakdown</h3>
                {(() => {
                  const lateFee = calculateLateFee(selectedFee);
                  const dueDate = selectedFee.dueDate?.toDate ? selectedFee.dueDate.toDate() : new Date(selectedFee.dueDate);
                  const now = new Date();
                  const isLate = selectedFee.dueDate && now > dueDate;
                  const daysLate = isLate ? Math.floor((now - dueDate) / (1000 * 60 * 60 * 24)) : 0;
                  
                  return (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Original Fee:</span>
                        <span className="text-gray-900 dark:text-white">₹{selectedFee.amount?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Already Paid:</span>
                        <span className="text-green-600 dark:text-green-400">₹{(selectedFee.paidAmount || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Outstanding:</span>
                        <span className="text-gray-900 dark:text-white">₹{selectedFee.outstandingAmount?.toLocaleString()}</span>
                      </div>
                      {selectedFee.lateFeeEnabled && isLate && lateFee > 0 && (
                        <>
                          <div className="border-t border-gray-200 dark:border-gray-600 my-2"></div>
                          <div className="flex justify-between text-orange-600 dark:text-orange-400">
                            <span>Late Fee ({daysLate} days late):</span>
                            <span>₹{lateFee.toLocaleString()}</span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedFee.lateFeeType === 'fixed' && 'Fixed late fee'}
                            {selectedFee.lateFeeType === 'perDay' && `₹${selectedFee.lateFeeAmount}/day × ${daysLate} days`}
                            {selectedFee.lateFeeType === 'percentage' && `${selectedFee.lateFeePercentage}% of fee amount`}
                            {selectedFee.lateFeeType === 'slab' && 'Slab-based late fee'}
                          </div>
                          <div className="border-t border-gray-200 dark:border-gray-600 my-2"></div>
                          <div className="flex justify-between font-semibold text-red-600 dark:text-red-400">
                            <span>Total Due (with late fee):</span>
                            <span>₹{(selectedFee.outstandingAmount + lateFee).toLocaleString()}</span>
                          </div>
                        </>
                      )}
                      {(!selectedFee.lateFeeEnabled || !isLate || lateFee === 0) && (
                        <div className="border-t border-gray-200 dark:border-gray-600 my-2 pt-2">
                          <div className="flex justify-between font-semibold">
                            <span className="text-gray-700 dark:text-gray-300">Total Due:</span>
                            <span className="text-gray-900 dark:text-white">₹{selectedFee.outstandingAmount?.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount (₹) *
                </label>
                <input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                />
                {selectedFee && selectedFee.lateFeeEnabled && (
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentForm({ ...paymentForm, amount: selectedFee.outstandingAmount })}
                      className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Pay Outstanding Only
                    </button>
                    {calculateLateFee(selectedFee) > 0 && (
                      <button
                        type="button"
                        onClick={() => setPaymentForm({ ...paymentForm, amount: selectedFee.outstandingAmount + calculateLateFee(selectedFee) })}
                        className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                      >
                        Pay with Late Fee
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Method *
                </label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Transaction ID
                </label>
                <input
                  type="text"
                  value={paymentForm.transactionId}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowPaymentForm(false)}
                disabled={recordingPayment}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={recordingPayment}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {recordingPayment ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Recording...
                  </>
                ) : (
                  'Record Payment'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FeeManagement;

